import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.#preserveVelortenPresentation(newMediaGallery);
        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Carries client-owned VELORTEN gallery state onto server-rendered variant
   * markup before replacement. Applying the collapsed classes synchronously
   * prevents a transient full-height gallery from disturbing scroll anchoring.
   *
   * @param {Element} newMediaGallery
   */
  #preserveVelortenPresentation(newMediaGallery) {
    if (!(newMediaGallery instanceof HTMLElement)) return;

    for (const attribute of ['data-velorten-initialized', 'data-velorten-expanded']) {
      const value = this.getAttribute(attribute);
      if (value != null) newMediaGallery.setAttribute(attribute, value);
    }

    const currentGrid = this.querySelector('.media-gallery__grid');
    const replacementGrid = newMediaGallery.querySelector('.media-gallery__grid');
    if (!(currentGrid instanceof HTMLElement) || !(replacementGrid instanceof HTMLElement)) return;

    if (currentGrid.id && !replacementGrid.id) replacementGrid.id = currentGrid.id;

    const isToggle = (node) =>
      node instanceof HTMLElement && node.classList.contains('velorten-pdp__media-toggle-wrap');
    const currentItems = Array.from(currentGrid.children).filter((item) => !isToggle(item));
    const replacementItems = Array.from(replacementGrid.children).filter((item) => !isToggle(item));

    replacementItems.forEach((item, index) => {
      item.classList.toggle(
        'velorten-pdp__media-hidden',
        currentItems[index]?.classList.contains('velorten-pdp__media-hidden') === true
      );
    });

    const currentToggle = currentGrid.querySelector('.velorten-pdp__media-toggle-wrap');
    const replacementToggle = replacementGrid.querySelector('.velorten-pdp__media-toggle-wrap');
    if (currentToggle instanceof HTMLElement && !replacementToggle) {
      const toggleClone = currentToggle.cloneNode(true);
      if (toggleClone instanceof HTMLElement) {
        toggleClone.querySelector('.velorten-pdp__media-toggle')?.removeAttribute('data-velorten-bound');
        replacementGrid.append(toggleClone);
      }
    }
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
